interface AnalyticsEvent {
  type: 'page_visit' | 'modal_opened' | 'modal_submitted'
  timestamp: number
  sessionId: string
}

const STORAGE_KEY = 'optin_analytics'

// Generate a simple session ID
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('session_id')
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    sessionStorage.setItem('session_id', sessionId)
  }
  return sessionId
}

// Get all events from localStorage
export function getAnalyticsEvents(): AnalyticsEvent[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    return data ? JSON.parse(data) : []
  } catch (error) {
    console.error('Error reading analytics:', error)
    return []
  }
}

// Save events to localStorage
function saveEvents(events: AnalyticsEvent[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events))
  } catch (error) {
    console.error('Error saving analytics:', error)
  }
}

// Track an event
export function trackEvent(
  type: 'page_visit' | 'modal_opened' | 'modal_submitted',
): void {
  const events = getAnalyticsEvents()
  const newEvent: AnalyticsEvent = {
    type,
    timestamp: Date.now(),
    sessionId: getSessionId(),
  }
  events.push(newEvent)
  saveEvents(events)
}

// Calculate CTR metrics
export interface CTRMetrics {
  totalVisits: number
  totalModalOpens: number
  totalSubmissions: number
  ctr: number
  modalOpenRate: number
  conversionRate: number
  uniqueVisits: number
  uniqueSubmissions: number
  uniqueCTR: number
}

export function calculateCTR(startDate?: Date, endDate?: Date): CTRMetrics {
  const events = getAnalyticsEvents()

  // Filter by date range if provided
  const filteredEvents = events.filter((event) => {
    if (startDate && event.timestamp < startDate.getTime()) return false
    if (endDate && event.timestamp > endDate.getTime()) return false
    return true
  })

  // Count total events
  const totalVisits = filteredEvents.filter(
    (e) => e.type === 'page_visit',
  ).length
  const totalModalOpens = filteredEvents.filter(
    (e) => e.type === 'modal_opened',
  ).length
  const totalSubmissions = filteredEvents.filter(
    (e) => e.type === 'modal_submitted',
  ).length

  // Count unique sessions
  const uniqueVisitSessions = new Set(
    filteredEvents
      .filter((e) => e.type === 'page_visit')
      .map((e) => e.sessionId),
  ).size
  const uniqueSubmissionSessions = new Set(
    filteredEvents
      .filter((e) => e.type === 'modal_submitted')
      .map((e) => e.sessionId),
  ).size

  return {
    totalVisits,
    totalModalOpens,
    totalSubmissions,
    ctr: totalVisits > 0 ? (totalSubmissions / totalVisits) * 100 : 0,
    modalOpenRate: totalVisits > 0 ? (totalModalOpens / totalVisits) * 100 : 0,
    conversionRate:
      totalModalOpens > 0 ? (totalSubmissions / totalModalOpens) * 100 : 0,
    uniqueVisits: uniqueVisitSessions,
    uniqueSubmissions: uniqueSubmissionSessions,
    uniqueCTR:
      uniqueVisitSessions > 0
        ? (uniqueSubmissionSessions / uniqueVisitSessions) * 100
        : 0,
  }
}

// Clear all analytics data
export function clearAnalytics(): void {
  localStorage.removeItem(STORAGE_KEY)
}
